<script setup lang="ts">
import { defineEmits, defineProps, ref, watch } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { ClockIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  modelValue: Date,
  label: String,
  errorMessage: String,
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const date = ref<Date | null>(props.modelValue ? new Date(props.modelValue) : null)
const time = ref(
  props.modelValue
    ? {
        hours: new Date(props.modelValue).getHours(),
        minutes: new Date(props.modelValue).getMinutes(),
      }
    : { hours: 0, minutes: 0 },
)

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal) {
      date.value = new Date(newVal)
      time.value.hours = newVal.getHours()
      time.value.minutes = newVal.getMinutes()
    }
  },
  { immediate: true },
)

const combineDateAndTime = () => {
  if (!date.value || !time.value) return
  const result = new Date(date.value)

  result.setHours(time.value.hours, time.value.minutes, 0, 0)

  emit('update:modelValue', result)
}
</script>

<template>
  <div class="grid grid-cols-5 items-center gap-1 mb-1">
    {{ label }}
    <Datepicker
      v-model="date"
      locale="fr"
      format="dd/MM/yyyy"
      :disabled-week-days="[6, 0]"
      auto-apply
      :range="false"
      :multi-dates="false"
      :enable-time-picker="false"
      @update:modelValue="combineDateAndTime"
      teleport="body"
      class="col-span-2"
      :disabled="disabled"
    />

    <Datepicker
      v-model="time"
      minutes-increment="15"
      time-picker
      :auto-apply="true"
      @update:modelValue="combineDateAndTime"
      teleport="body"
      class="col-span-2"
      :disabled="disabled"
    >
      <template #input-icon><component :is="ClockIcon" class="w-4 ms-3" /> </template
    ></Datepicker>
  </div>
  <span v-if="errorMessage" class="text-red-500 text-sm">{{ errorMessage }}</span>
</template>

<style>
:root {
  --dp-border-radius: 0.5rem !important;
  --dp-font-size: 0.75rem;
  --dp-time-font-size: 0.75rem;
  --dp-menu-width: 258.3999938964844px;
}

.dp__overlay[aria-label='Time picker'] {
  height: fit-content !important;
}
</style>
